import { useState, useEffect } from 'react';
import { Eye, EyeOff, KeyRound } from 'lucide-react';
import { isPasswordProtectionEnabled } from '../data/site_settings';

interface PasswordProtectionProps {
  children: React.ReactNode;
}

const INACTIVITY_TIMEOUT = 10 * 60 * 1000; // 10 minutes in milliseconds
const AUTH_KEY = 'mrg-authenticated';
const LAST_ACTIVE_KEY = 'mrg-last-active';

export default function PasswordProtection({ children }: PasswordProtectionProps) {
  // On the server, always render children (SSR should not be blocked by password protection)
  const isServer = typeof window === 'undefined';

  const [isAuthenticated, setIsAuthenticated] = useState(isServer ? true : false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(!isServer);

  // Check if the user is already authenticated
  useEffect(() => {
    if (isServer) return;
    checkAuthentication();
    setIsLoading(false);
  }, []);

  // Setup activity tracking
  useEffect(() => {
    if (isAuthenticated) {
      // Update last active timestamp
      updateLastActiveTime();
      
      // Setup event listeners for user activity
      const activityEvents = [
        'mousedown', 'mousemove', 'keypress', 
        'scroll', 'touchstart', 'click', 'keydown'
      ];
      
      const handleUserActivity = () => {
        updateLastActiveTime();
      };
      
      // Add all event listeners
      activityEvents.forEach(event => {
        window.addEventListener(event, handleUserActivity);
      });
      
      // Setup interval to check for inactivity
      const inactivityCheckInterval = setInterval(() => {
        checkAuthentication();
      }, 30000); // Check every 30 seconds
      
      // Cleanup function
      return () => {
        activityEvents.forEach(event => {
          window.removeEventListener(event, handleUserActivity);
        });
        clearInterval(inactivityCheckInterval);
      };
    }
  }, [isAuthenticated]);

  const checkAuthentication = () => {
    if (typeof localStorage === 'undefined') return;
    const authenticated = localStorage.getItem(AUTH_KEY) === 'true';
    
    if (authenticated) {
      const lastActiveTime = localStorage.getItem(LAST_ACTIVE_KEY);
      if (lastActiveTime) {
        const inactiveTime = Date.now() - parseInt(lastActiveTime, 10);
        
        if (inactiveTime > INACTIVITY_TIMEOUT) {
          // Log out if inactive for too long
          localStorage.removeItem(AUTH_KEY);
          setIsAuthenticated(false);
        } else {
          setIsAuthenticated(true);
        }
      } else {
        // If no last active time, update it now
        updateLastActiveTime();
        setIsAuthenticated(true);
      }
    } else {
      setIsAuthenticated(false);
    }
  };

  const updateLastActiveTime = () => {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(LAST_ACTIVE_KEY, Date.now().toString());
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (password === 'ARG123') {
      localStorage.setItem(AUTH_KEY, 'true');
      updateLastActiveTime();
      setIsAuthenticated(true);
      setError('');
    } else {
      setError('Incorrect password. Please try again.');
      setPassword('');
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // If password protection is disabled, just show children
  if (!isPasswordProtectionEnabled()) {
    return <>{children}</>;
  }

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-white flex items-center justify-center">
        <div className="relative w-12 h-12">
          <div className="absolute h-12 w-12 rounded-full animate-spin border-4 border-solid border-black border-t-transparent"></div>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <>{children}</>;
  }

  return (
    <div className="fixed inset-0 bg-gray-50 flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-md bg-white shadow-lg p-8 border border-gray-200">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-1 text-primary">Rish Web Services</h1>
          <p className="text-gray-600 mt-2 text-lg">Please enter the password to access the site</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-0">
              {error}
            </div>
          )}
          
          <div>
            <label htmlFor="password" className="block text-base font-medium text-gray-700 mb-1">
              Password
            </label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                <KeyRound size={18} />
              </div>
              
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="block w-full border border-gray-300 pl-10 pr-10 py-3 focus:outline-none focus:ring-1 focus:ring-gray-800 focus:border-gray-800"
                placeholder="Enter password"
                autoComplete="off"
              />
              
              <button 
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-black text-lg"
                onClick={togglePasswordVisibility}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          
          <button
            type="submit"
            className="w-full bg-black text-white px-6 py-3 font-medium hover:bg-gray-800 transition-colors duration-300 border border-gray-300 shadow-md hover:shadow-gray-500/20  text-lg"
          >
            Access Site
          </button>
        </form>
      </div>
      
      <div className="mt-8 text-center text-gray-500 text-base">
        <p>© {new Date().getFullYear()} Rish Web Services. All rights reserved.</p>
      </div>
    </div>
  );
}
